/* ═══════════════════════════════════════════════════════════════════════════
   NORWAY — holiday, sickness and care leave, as figures

   Every number here is in a statute, and every one of them carries the
   paragraph it came from. Nothing in this file is a policy of Avtale.net's:
   a platform that invented an entitlement would be telling an employee they
   have a right they do not have, or — worse — telling them they do not have
   one they do.

   ── Why this is a file and not a constant in a screen ─────────────────────

   Because the trade does not decide it and the platform does not decide it:
   the country of operation does. A Swedish salon on this platform is under
   semesterlagen, not ferieloven, and its figures are different — 25 dagar,
   no six-day week, a different qualifying year. So the shape here is the
   shape every country answers in, and a second country is a second file next
   to this one rather than a rewrite of everything above it. No screen in
   `surfaces/` may name a figure; they ask the rule set.

   ── What is a "virkedag" ──────────────────────────────────────────────────

   The one thing about ferieloven that catches everybody out. A virkedag is
   every day of the week except Sunday and the statutory holidays, so a week
   is SIX of them, not five — which is why the statutory holiday is 25
   virkedager and people still call it four weeks and a day. Five weeks, the
   ordinary tariff figure, is 30. Getting this wrong understates somebody's
   holiday by a sixth, so the conversion is here, once, and `domain/leave.js`
   is the only thing that does it.

   ── What is deliberately NOT here ─────────────────────────────────────────

   Feriepenger. The rates are known — 10,2 % of last year's pay, 12 % on a
   five-week agreement, and higher over 60 — but a figure calculated from
   somebody's salary is payroll, and this platform does not hold salaries and
   should not start. The entitlement in DAYS is a rota question and belongs
   here; what those days are worth in kroner belongs to whoever runs the
   payroll, and the screens say so rather than computing a number nobody
   should rely on.

   Sources, all read September 2026: ferieloven §§ 5, 6, 7;
   folketrygdloven §§ 8-19, 8-24 and chapter 9; arbeidsmiljøloven § 10-3.
   ═══════════════════════════════════════════════════════════════════════ */

export default {
  code: 'NO',
  name: 'Norway',
  /* The word the screens use for a self-reported sick day. It is not
     "self-certification" in ordinary English and translating it loses the
     thing: egenmelding is a named statutory instrument, like "P45". */
  words: {
    self: 'Egenmelding',
    selfWhat: 'You tell your employer you are ill. It is a notification, not '
      + 'an application — nobody approves it.',
    child: 'Sick child',
    childWhat: 'Omsorgsdager. Also a notification, and also not approved.',
    holiday: 'Holiday',
    holidayWhat: 'Your employer decides when, after discussing it with you, '
      + 'and must tell you in good time.',
    unit: 'virkedager',
    unitWhat: 'Every day but Sunday and a public holiday. A week is six of '
      + 'them, which is why four weeks and a day comes to 25.',
  },

  /* ── Ferieloven ──────────────────────────────────────────────────────── */
  holiday: {
    act: 'ferieloven',
    perWeek: 6,            // § 5 — a virkedag is any day but Sunday
    days: 25,              // § 5 (1)  four weeks and a day
    extraDays: 6,          // § 5 (2)  one more week
    extraFromAge: 60,      // § 5 (2)  from the year they turn 60
    mainDays: 18,          // § 7 (1)  may be DEMANDED inside the window
    mainFrom: '06-01',     // § 7 (1)
    mainTo: '09-30',       // § 7 (1)
    restDays: 7,           // § 7 (2)  and taken in one piece
    noticeDays: 60,        // § 6 (2)  two months, unless something unforeseen
    carryOverMax: 12,      // § 7 (3)  by written agreement
    cites: {
      days: 'ferieloven § 5 (1)',
      extra: 'ferieloven § 5 (2)',
      main: 'ferieloven § 7 (1)',
      rest: 'ferieloven § 7 (2)',
      notice: 'ferieloven § 6 (2)',
      carry: 'ferieloven § 7 (3)',
    },
    /* The bit a platform must not quietly decide for a business. Five weeks
       is extremely common and comes from a tariffavtale or the contract —
       never from the statute — so it is the business's own fact, switched on
       per business, and the screen says where it comes from. */
    commonAgreed: 30,
    agreedWhere: 'a tariffavtale or the contract of employment, never the Act',
  },

  /* ── Folketrygdloven, sickness ───────────────────────────────────────── */
  sick: {
    act: 'folketrygdloven',
    employerDays: 16,      // § 8-19  arbeidsgiverperioden, calendar days
    self: {
      daysPerSpell: 3,     // § 8-24 (1)  calendar days at a time
      spellsPer12Months: 4,// § 8-24 (1)  four occasions in a rolling year
      qualifyingMonths: 2, // § 8-24 (2)  two months in the job first
    },
    /* § 8-24 (3). An employer may give more than the statute — up to the
       whole employer period — and an IA workplace commonly does. It is an
       AGREEMENT: the employer must discuss it with the tillitsvalgte first,
       and the screen that switches it on says so rather than implying the
       agreement exists because a toggle is green. */
    extended: {
      daysPerSpell: 8,
      daysPer12Months: 24,
      requires: 'a written arrangement, after drøfting with the tillitsvalgte',
    },
    cites: {
      employer: 'folketrygdloven § 8-19',
      self: 'folketrygdloven § 8-24 (1)',
      qualifying: 'folketrygdloven § 8-24 (2)',
      extended: 'folketrygdloven § 8-24 (3)',
      after: 'folketrygdloven § 8-7',
    },
    afterSelf: 'Past what egenmelding covers, it takes a sykmelding from a '
      + 'doctor. Nothing on this screen replaces one.',
  },

  /* ── Folketrygdloven chapter 9, a sick child ─────────────────────────── */
  child: {
    act: 'folketrygdloven',
    days: 10,              // ch. 9 — one or two children
    daysFrom: 3,           // three or more children…
    daysMany: 15,          // …makes it 15
    soleCarerTimes: 2,     // alone with the care: double, so 20 or 30
    chronicExtra: 10,      // a chronically ill or disabled child: ten more
    childUnder: 12,        // to the end of the year the child turns 12
    chronicUnder: 18,      // to the end of the year they turn 18, if chronic
    employerDays: 10,      // the employer pays the first ten each year
    cites: {
      days: 'folketrygdloven kap. 9',
      employer: 'folketrygdloven kap. 9',
    },
    what: 'Omsorgsdager are per calendar year and per parent, and they are '
      + 'for the child being ill or the person who normally minds them being '
      + 'ill — not for a day off.',
  },

  /* ── Bedriftshelsetjeneste ───────────────────────────────────────────
     The thing almost everybody gets wrong, in both directions.

     It is NOT true that every business must have one. The duty to be
     attached to an APPROVED bedriftshelsetjeneste falls on the trades named
     in forskrift om organisering, ledelse og medvirkning chapter 13 — and
     on everybody else only where the risk assessment says so
     (arbeidsmiljøloven § 3-3). Telling a law firm it is breaking the law is
     as wrong as telling a building contractor it is fine, and a platform
     that did the first to sell a subscription would deserve everything that
     came of it.

     The list is by næringskode, not by the name of the trade, so the
     registry can say what is NORMALLY true of a trade and no more. Which
     code a given business actually has is that business's own fact, and
     every screen says so and points at Arbeidstilsynet rather than
     answering for them.

     Two further things the screens have to carry:

       The provider must be APPROVED by Arbeidstilsynet. An unapproved
       supplier does not discharge the duty, so "approved" is a fact about a
       provider that has to be checked rather than claimed.

       The service is confidential. What an employee consults it about is
       between them and the health personnel; the employer buys the service
       and does not get the contents. That is not a nicety to be traded away
       for a nicer dashboard — it is the whole basis on which anybody uses
       it. */
  health: {
    act: 'forskrift om organisering, ledelse og medvirkning',
    cite: 'forskrift om organisering, ledelse og medvirkning kap. 13',
    riskCite: 'arbeidsmiljøloven § 3-3',
    approvedBy: 'Arbeidstilsynet',
    listedWhat: 'Trades of this kind are named in the regulation, so the duty '
      + 'to be attached to an approved occupational health service applies '
      + 'whatever the risk assessment says.',
    riskWhat: 'Trades of this kind are not named in the regulation. The duty '
      + 'still applies where the risk assessment says it does — it is the '
      + 'risk that decides, not the absence of a name on a list.',
    byCode: 'The regulation lists næringskoder, not trade names. Which code '
      + 'this business has is its own fact — check it against Arbeidstilsynet '
      + 'rather than against this screen.',
    approvedWhat: 'Only an approved provider discharges the duty. An '
      + 'arrangement with an unapproved supplier is not a bedriftshelsetjeneste '
      + 'in the eyes of the regulation.',
    /* The sentence that has to be on the employee's screen and on the
       employer's, in the same words. */
    confidential: 'What you talk to them about is between you and them. Your '
      + 'employer pays for the service and is never told the reason for an '
      + 'appointment, what was said in it, or what came of it.',
    employerSees: 'That the subscription is used, and how much of it. Never '
      + 'by whom, and never what about.',
    payer: 'The business pays for what the service does.',
  },

  /* ── Merverdiavgift ──────────────────────────────────────────────────
     The rates are statutory and belong here. WHICH ONE APPLIES TO A GIVEN
     SERVICE IS NOT, and nothing on this platform will guess it.

     That is not caution for its own sake. The boundaries are genuinely
     narrow and genuinely expensive:

       A restaurant serving food at a table charges 25 %. The same dish in a
       box to take away is 15 %, because it is not being eaten on the
       premises. One kitchen, two rates, and the difference is the lid.

       Health services are exempt entirely — outside the Act rather than
       zero-rated — but a physiotherapist selling a support belt is charging
       25 % on it.

       And a business under the registration threshold charges nothing at
       all, and must not: invoicing VAT while unregistered is its own
       problem to unwind.

     A trade code cannot answer any of those. The business can, it is their
     own fact, and the screens ask them for it once per service and then say
     where the figure came from. A rate this platform guessed would be a
     rate somebody filed a return on. */
  vat: {
    act: 'merverdiavgiftsloven',
    cite: 'merverdiavgiftsloven § 5-1 flg.',
    thresholdCite: 'merverdiavgiftsloven § 2-1',
    /* Below this in twelve months, a business is not registered and charges
       nothing. The figure is the ordinary one; a charitable or non-profit
       body has its own, higher, and this platform does not decide which a
       business is. */
    threshold: 50000,
    thresholdWhat: 'Under this in a rolling twelve months and a business is '
      + 'not registered, charges no VAT, and must not put it on an invoice.',
    rates: [
      { id: 'standard', pct: 25,
        label: 'Standard',
        what: 'Most goods and services. Serving food at a table is this, not the food rate.' },
      { id: 'food', pct: 15,
        label: 'Food',
        what: 'Næringsmidler — groceries, and food to take away rather than eat there.' },
      { id: 'low', pct: 12,
        label: 'Low',
        what: 'Passenger transport, accommodation, cinema and museum admission, camping.' },
      { id: 'exempt', pct: 0,
        label: 'Outside the Act',
        what: 'Health services, teaching, and the other exemptions. Not the same '
          + 'as nought per cent — nothing is deducted either.' },
      { id: 'zero', pct: 0,
        label: 'Zero-rated',
        what: 'Inside the Act at 0 %, such as exports. Input VAT is still deducted.' },
    ],
    /* Said on the screen wherever a rate is chosen. */
    whose: 'Which rate applies to a service is the business\'s own fact and '
      + 'this platform will not guess it from a trade. Ask your accountant '
      + 'once, set it here, and the export stops being a judgement call.',
    unset: 'No rate set — this service is left out of the export rather than '
      + 'exported at a rate nobody chose.',
  },

  /* ── Bokføring: the audit trail, and what a receipt must say ─────────
     The two-way control trail Stein asked for is bokføringsloven § 4 nr. 7
     and § 6: it must be possible to follow a booked entry from its
     documentation, through the specifications, to the reported figures —
     AND to start from the reported figure and find the individual entry it
     came from. Both directions. A report that only totals upwards is half
     of it.

     What makes that possible in practice is a SEQUENTIAL number on every
     sale. The number is what a report points back down at and what a sale
     points up with, and a gap in the series is the thing an auditor looks
     for first.

     ── What this build is, and is not ──────────────────────────────────
     A webshop, not a till — and that is a legal distinction, not a
     marketing one.

     Kontantsalg is defined in bokføringsforskriften § 5-3-1 bokstav a as a
     sale where the buyer's obligation is settled AT DELIVERY, by card or
     cash — and the same letter goes on to say, in as many words, that a
     sale over the internet is not kontantsalg. Booking and paying on
     Avtale.net is a sale over the internet: the money moves when the
     customer books, days before anybody is delivered anything.

     So the kassasystem rules do not reach this platform at all. There is no
     produkterklæring to file, because a produkterklæring is what the
     supplier of a CASH REGISTER files, and nothing here registers a cash
     sale. An earlier version of this file said the opposite and it was
     simply wrong.

     What a remote sale owes instead is an ordinary salgsdokument under
     bokføringsforskriften kap. 5 — the seller, the date, a sequential
     number, the lines, the sums with VAT by rate. That is what
     domain/salesdoc.js produces, and `receiptNeeds` is the list.

     ── The one place the till rules do bite ─────────────────────────────
     A customer who pays at the counter when they arrive IS a kontantsalg,
     and that sale belongs in the SALON'S own till — their obligation,
     their system, at their premises. This platform's note that somebody
     chose to pay in the salon is a note about an expected payment, not a
     registered cash sale, and `tillLine` is what the screens say about
     that boundary so a tenant does not read our record as their till
     roll. */
  books: {
    act: 'bokføringsloven',
    traceCite: 'bokføringsloven §§ 4 nr. 7 og 6',
    traceWhat: 'A booked entry must be traceable from its documentation '
      + 'through to the reported figures, and from the reported figure back '
      + 'to the entry it came from. Both directions.',
    /* Primary documentation: five years after the end of the financial
       year. */
    keepYears: 5,
    keepCite: 'bokføringsloven § 13',
    keepWhat: 'Primary documentation is kept five years after the end of the '
      + 'financial year it belongs to.',

    /* What a sales receipt has to carry. Every one of these is on the
       document this build produces — see domain/salesdoc.js — which is why
       the list is here rather than in a component. */
    receiptCite: 'bokføringsforskriften kap. 5',
    receiptNeeds: [
      ['seller', 'The seller\'s name and organisation number, with “MVA” where registered'],
      ['when', 'The date and the time'],
      ['number', 'A sequential receipt number'],
      ['lines', 'What was sold, and how many'],
      ['sums', 'The price, the total, and the VAT split by rate'],
      ['paid', 'How it was paid — cash, card or mobile'],
    ],

    /* Why the till rules do not reach a sale made over the internet. */
    remoteCite: 'bokføringsforskriften § 5-3-1 bokstav a',
    remoteWhat: 'Kontantsalg is a sale settled AT DELIVERY by card or cash, '
      + 'and the same provision says a sale over the internet is not '
      + 'kontantsalg. A booking paid for on Avtale.net is a remote sale: the '
      + 'money moves when the customer books.',
    /* What it IS, rather than a list of rules it is outside. A tenant does
       not need the names of obligations they do not have. */
    remoteSo: 'What a remote sale owes is an ordinary sales document under '
      + 'bokføringsforskriften kap. 5: the seller, the date, a sequential '
      + 'number, the lines, and the sums with VAT by rate. Every one of those '
      + 'is on this document.',

    /* The boundary, stated so a tenant does not mistake one for the other. */
    tillCite: 'bokføringsloven § 10a',
    tillLine: 'A customer who pays at your counter when they arrive IS a cash '
      + 'sale, and it belongs in your own till — your obligation, your '
      + 'system, at your premises. What this screen holds for those is a note '
      + 'that somebody chose to pay in the salon, which is an expected '
      + 'payment and not a registered cash sale.',
    tillWho: 'A business with more than 50 000 kr of cash sales in a year '
      + 'must use a declared cash register system for them. Sales taken '
      + 'through Avtale.net are not among those, because they are not cash '
      + 'sales.',
  },

  /* ── Arbeidsmiljøloven, the shift plan ───────────────────────────────── */
  plan: {
    act: 'arbeidsmiljøloven',
    aheadDays: 14,         // § 10-3 — drawn up with the reps, out two weeks before
    cite: 'arbeidsmiljøloven § 10-3',
    what: 'The plan is drawn up together with the tillitsvalgte and has to be '
      + 'available to the people on it at least two weeks before it starts.',
  },
};
